import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputNumber } from "primereact/inputnumber";

import { Button, TextInputField } from "../../../helpers";

import { updateVPAdjustedAmount } from "../../../services/api/adjustmentNotes";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { fetchSellerDebitNotes } from "../../../services/redux/reducers/sellerDebitNote/sellerDebitNote";
import { useDispatch } from "react-redux";

import "./UpdateVPAdjustmentModal.css";


const UpdateVPAdjustmentModal = ({ visible, onHide, selectedRow, paginationParams, searchParams }) => {
    const [isFullyAdjusted, setIsFullyAdjusted] = useState(true);
    const [adjustedAmount, setAdjustedAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [remarks, setRemarks] = useState("");

    const usersData = useSelector(state => state.userDetails);
    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedRow && visible) {
            setIsFullyAdjusted(true);
            setAdjustedAmount(0);
            setRemarks("");
        }
    }, [selectedRow, visible]);

    const handleSubmit = async () => {
        if (!selectedRow) return;

        setIsLoading(true);
        try {
            const data = {
                isFullyAdjusted: isFullyAdjusted,
                adjustedAmount: isFullyAdjusted ? 0 : adjustedAmount,
                remarks: remarks || (isFullyAdjusted ? "VP fully adjusted for this debit note" : `VP partially adjusted by Rs ${adjustedAmount}`)
            };

            const response = await updateVPAdjustedAmount(selectedRow.adjustmentNoteId, data);
            
            if (response?.failure) {
                dispatch(raiseToast({
                    severity: 'error', 
                    summary: 'Update Failed', 
                    detail: response.data?.error || 'Failed to update VP adjustment amount', 
                    life: 3000
                }));
            } else {
                dispatch(raiseToast({
                    severity: 'success', 
                    detail: 'VP adjustment amount updated successfully', 
                    life: 3000
                }));
                
                // Refresh the data
                const adjustmentNotesParams = { 
                    paginator: true, 
                    pageNo: paginationParams.pageNo, 
                    recordsPerPage: paginationParams.recordsPerPage,
                    limitedPagination: true,
                    getCount: false,
                    ...searchParams
                };
                if (adjustmentNotesParams.searchBy !== "all") {
                    adjustmentNotesParams[adjustmentNotesParams.searchBy] = adjustmentNotesParams.searchText.trim();
                }
                dispatch(fetchSellerDebitNotes({...adjustmentNotesParams, adjustmentTypeIds: [3, 9, 10] }));
                
                onHide();
            }
        } catch (error) {
            console.error("Error updating VP adjustment amount:", error);
            dispatch(raiseToast({
                severity: 'error', 
                summary: 'Update Failed', 
                detail: 'An error occurred while updating VP adjustment amount', 
                life: 3000
            }));
        } finally {
            setIsLoading(false);
        }
    };

    const getRemainingAmount = () => {
        if (!selectedRow) return 0;
        const currentAdjusted = selectedRow.vpAdjustedAmount || 0;
        const totalAmount = selectedRow.totalAmountWithTax || 0;
        return Math.max(0, totalAmount - currentAdjusted);
    };

    const footer = (
        <div className="flex justify-content-end p-0">
            <Button 
                label="Cancel" 
                className="p-button-outlined p-button-secondary" 
                onClick={onHide}
                disabled={isLoading}
            />
            <Button 
                label="Update VP Adjustment" 
                onClick={handleSubmit}
                loading={isLoading}
                className="p-button-success"
            />
        </div>
    );

    return (
        <Dialog 
            header={<span style={{ fontWeight: '600' }}>Update VP Adjustment Amount</span>}
            visible={visible} 
            style={{ width: '50vw', maxWidth: '700px' }} 
            onHide={onHide}
            footer={footer}
            modal
            className="vp-adjustment-modal"
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        >
            {selectedRow && (
                <div className="flex flex-column gap-3 p-3">
                    
                    <div className="flex flex-column gap-2">
                        <div className="flex align-items-center gap-2 mb-2">
                            <h4 className="m-0" style={{ color: '#1f2937', fontWeight: '600' }}>
                                Debit Note: {selectedRow.docNo}
                            </h4>
                        </div>
                        
                        <div className="grid">
                            <div className="col-12 md:col-4">
                                <div className="text-center p-2">
                                    <div className="text-500 text-sm mb-1">Total Amount</div>
                                    <div className="text-lg font-bold text-900">
                                        {selectedRow.totalAmountWithTax || 0}
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="text-center p-2">
                                    <div className="text-500 text-sm mb-1">Currently Adjusted</div>
                                    <div className="text-lg font-bold text-blue-600">
                                        {selectedRow.vpAdjustedAmount || 0}
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="text-center p-2">
                                    <div className="text-500 text-sm mb-1">Remaining Amount</div>
                                    <div className="text-lg font-bold text-orange-600">
                                        {getRemainingAmount()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-column gap-2">
                        <div>
                            <label className="block text-900 font-semibold mb-2">
                                <i className="pi pi-cog mr-2"></i>
                                Adjustment Type
                            </label>
                            <div className="flex flex-column gap-2">
                                <div className="flex align-items-center p-2 cursor-pointer border-round" 
                                     style={{ 
                                         border: isFullyAdjusted ? '1px solid #22c55e' : '1px solid #e5e7eb',
                                         backgroundColor: isFullyAdjusted ? '#f0fdf4' : '#ffffff'
                                     }}
                                     onClick={() => setIsFullyAdjusted(true)}>
                                    <RadioButton 
                                        inputId="fullyAdjusted" 
                                        name="adjustmentType" 
                                        value={true} 
                                        onChange={(e) => setIsFullyAdjusted(e.value)} 
                                        checked={isFullyAdjusted} 
                                    />
                                    <label htmlFor="fullyAdjusted" className="ml-2 cursor-pointer font-medium">
                                        <div className="text-900">Fully Adjusted</div>
                                        <div className="text-500 text-sm">Mark the entire amount as adjusted</div>
                                    </label>
                                </div>
                                <div className="flex align-items-center p-2 cursor-pointer border-round"
                                     style={{ 
                                         border: !isFullyAdjusted ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                                         backgroundColor: !isFullyAdjusted ? '#eff6ff' : '#ffffff'
                                     }}
                                     onClick={() => setIsFullyAdjusted(false)}>
                                    <RadioButton 
                                        inputId="partiallyAdjusted" 
                                        name="adjustmentType" 
                                        value={false} 
                                        onChange={(e) => setIsFullyAdjusted(e.value)} 
                                        checked={!isFullyAdjusted} 
                                    />
                                    <label htmlFor="partiallyAdjusted" className="ml-2 cursor-pointer font-medium">
                                        <div className="text-900">Partially Adjusted</div>
                                        <div className="text-500 text-sm">Adjust a specific amount</div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {!isFullyAdjusted && (
                            <div>
                                <label className="block text-900 font-semibold mb-2">
                                    Adjustment Amount
                                </label>
                                <div className="flex flex-column gap-1">
                                    <InputNumber 
                                        value={adjustedAmount} 
                                        onValueChange={(e) => setAdjustedAmount(e.value)} 
                                        mode="currency" 
                                        currency="INR" 
                                        locale="en-IN"
                                        min={0}
                                        max={getRemainingAmount()}
                                        placeholder="Enter amount to adjust"
                                        className="w-full"
                                    />
                                    <div className="flex align-items-center gap-1 text-500 text-sm">
                                        <i className="pi pi-info-circle"></i>
                                        <span>Maximum: {getRemainingAmount()}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-900 font-semibold mb-2">
                                <i className="pi pi-comment mr-2"></i>
                                Remarks
                            </label>
                            <TextInputField 
                                value={remarks} 
                                onChange={(e) => setRemarks(e.target.value)} 
                                placeholder={isFullyAdjusted ? "VP fully adjusted for this debit note" : "Enter remarks for partial adjustment"}
                                multiline
                                rows={2}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default UpdateVPAdjustmentModal; 